import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet";
import classnames from 'classnames';
import API from '../../functions/api';
import { gtmPageview, gtmPush } from '../../functions/gtm';
import Layout from '../../components/Layout';
import queryString from 'query-string';
import Select from '../../audi-ui-components/Select';
import IconSystemHelp from '../../audi-ui-components/icons/SystemHelp';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import _find from 'lodash/find';
import {
  PATH_SERVICE_PLAN_CALCULATOR,
  DEFAULT_THEME,
} from '../../constants';
import './service-plans.scss';

const ServicePlanCalculator = ({ location }) => {

  const query = queryString.parse(location.search);
  const theme = query.theme || DEFAULT_THEME;
  
  // const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedEngine, setSelectedEngine] = useState("");
  const [engineOpts, setEngineOpts] = useState([]);
  const [activeTab, setActiveTab] = useState("5yr");

  const getData = (list = []) => {
    API.post(
      `${process.env.RAZZLE_GQLAPI}`,
      {
          query: /* GraphQL */`query ($filter: ServicePricingDtoFilterInput, $sort: ServicePricingDtoSortInput!) {
            servicePricingList(order: [ $sort ], where: $filter, take: 50) {
              items {
                servicePricingId
                name
                status
                jsonData
                imageUrl
              }
              totalCount
            }
          }`,
          variables: {
            sort: {weight: "ASC"},
            filter: {status: {eq: true}}
          }
      }
    ).then((response) => {
      if (response.data.errors) {
        //
      } else if (response.data.data?.servicePricingList?.items) {
        let newList = [...list, ...response.data.data.servicePricingList.items];
        if (newList.length < response.data.data.servicePricingList.totalCount) {
          // load more
          getData(newList);
        } else {
          // got all
          for (let i=0; i<newList.length; i++) {
            let m = newList[i];
            m.engineTypes = m.jsonData ? JSON.parse(m.jsonData) : [];
            newList[i] = m;
          }
          setData(newList);
        }
      }
    }).catch((error) => {
      console.error(error);
    });
  }

  const getCurrentModel = (_m) => {
    var m = _m || selectedModel;
    return _find(data, {name: m});
  }

  const getCurrentEngine = (_m, _e) => {
    const model = getCurrentModel(_m);
    const e = _e || selectedEngine;
    if (model && e) {
      return _find(model.engineTypes, {engineType: e});
    }
    return null;
  }

  const getActiveTab = (engine) => {
    if (engine.threeYearServicePlan) { return "3yr"; }
    if (engine.fiveYearServicePlan) { return "5yr"; }
    if (engine.sixYearServicePlan) { return "6yr"; }
    if (engine.twoYearTopUpServicePlan) { return "2yr"; }
    return null;
  }

  const handleModelChange = (v) => {
    var model = getCurrentModel(v);
    var engine = model.engineTypes[0];
    var tab = getActiveTab(engine);
    var opts = model.engineTypes.map((e) => {return {label: e.engineType, value: e.engineType};});
    setSelectedModel(v);
    setSelectedEngine(engine.engineType);
    setEngineOpts(opts);
    setActiveTab(tab);
    gtmPush("form", "formSubmitSuccess", "servicePricingCalculator", {model: v, engine: engine.engineType});
  }

  const handleEngineChange = (v) => {
    var engine = getCurrentEngine(selectedModel, v);
    var tab = getActiveTab(engine);
    setSelectedEngine(v);
    setActiveTab(tab);
    gtmPush("form", "formSubmitSuccess", "servicePricingCalculator", {model: selectedModel, engine: v});
  }

  useEffect(() => {
    getData();
    gtmPageview(PATH_SERVICE_PLAN_CALCULATOR);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.length > 0) {
      handleModelChange(data[0].name);
    }
  }, [data]);
  
  const currentModel = getCurrentModel();
  const currentEngine = getCurrentEngine();
  const isEtron = selectedModel?.toLowerCase().indexOf("e-tron") > -1;
  
  return (
    <Layout ui={query.ui} embedded={query.embedded} wide={query.wide} theme={query.theme}>
      <Helmet>
        <title>Service Plan Calculator | Audi Australia</title>
      </Helmet>

      <main className="audi-forms__page price-calculator pt-3 pt-medium-5">
        <p className="aui-headline-5 mb-4">Select model and engine type</p>

        <div className="row">
          <div className="col-12 col-medium-4">
            <div className="form-field">
              <Select name="model"
                label="Model"
                value={selectedModel}
                options={data.map((m) => {return {label: m.name, value: m.name};})}
                onChange={handleModelChange}
                theme={theme === "dark" ? "light" : null}
              />
            </div>
            <div className="form-field" style={{display: engineOpts.length > 1 ? 'block' : 'none'}}>
              <Select name="engine"
                label="Engine"
                value={selectedEngine}
                options={engineOpts}
                onChange={handleEngineChange}
                theme={theme === "dark" ? "light" : null}
              />
            </div>
          </div>

          <div className="col-12 col-medium-8">
            {currentModel &&
              <img alt={currentModel.name} src={currentModel.imageUrl?.replace("http:", "https:")} style={{maxWidth: '100%'}} />
            }
            {currentEngine && <>
              <div className="row no-gutters service-plan-nav">
                {currentEngine.audiAdvantage && <div className="col">
                  <button className={classnames("service-plan-nav-item aui-button", {"active": activeTab === "adv"})} onClick={() => { setActiveTab("adv") }}>
                    <span>Audi Advantage</span>
                  </button>
                </div>}
                {currentEngine.threeYearServicePlan && <div className="col">
                  <button className={classnames("service-plan-nav-item aui-button", {"active": activeTab === "3yr"})} onClick={() => { setActiveTab("3yr") }}>
                    <span>3-Year Service Plan</span>
                  </button>
                </div>}
                {currentEngine.fiveYearServicePlan && <div className="col">
                  <button className={classnames("service-plan-nav-item aui-button", {"active": activeTab === "5yr"})} onClick={() => { setActiveTab("5yr") }}>
                    <span>5-Year Service Plan</span>
                  </button>
                </div>}
                {currentEngine.sixYearServicePlan && <div className="col">
                  <button className={classnames("service-plan-nav-item aui-button", {"active": activeTab === "6yr"})} onClick={() => { setActiveTab("6yr") }}>
                    <span>6-Year Service Plan</span>
                  </button>
                </div>}
                {currentEngine.twoYearTopUpServicePlan && <div className="col">
                  <button className={classnames("service-plan-nav-item aui-button", {"active": activeTab === "2yr"})} onClick={() => { setActiveTab("2yr") }}>
                    <span>2-Year Top-up Service Plan <OverlayTrigger placement="top" overlay={<Tooltip className={"tooltip theme-" + theme}>Top up your 3 Year Service Plan with the 4th and 5th services.</Tooltip>}><span><IconSystemHelp small /></span></OverlayTrigger></span>
                  </button>
                </div>}
              </div>

              {activeTab && <div>
                <div className="my-5">
                  {activeTab === "adv" && currentEngine.audiAdvantage &&
                    <p className="">
                      <span className="aui-headline-5">Audi Advantage <sup>*</sup>:</span>{` `}
                      <span className="aui-headline-4">{currentEngine.audiAdvantage}</span>
                    </p>
                  }
                  {activeTab === "3yr" && currentEngine.threeYearServicePlan &&
                    <p className="">
                      <span className="aui-headline-5">3-Year Service Plan <sup>**</sup>:</span>{` `}
                      <span className="aui-headline-4">{currentEngine.threeYearServicePlan}</span>
                    </p>
                  }
                  {activeTab === "5yr" && currentEngine.fiveYearServicePlan &&
                    <p className="">
                      <span className="aui-headline-5">5-Year Service Plan <sup>**</sup>:</span>{` `}
                      <span className="aui-headline-4">{currentEngine.fiveYearServicePlan}</span>
                    </p>
                  }
                  {activeTab === "6yr" && currentEngine.sixYearServicePlan &&
                    <p className="">
                      <span className="aui-headline-5">6-Year Service Plan <sup>**</sup>:</span>{` `}
                      <span className="aui-headline-4">{currentEngine.sixYearServicePlan}</span>
                    </p>
                  }
                  {activeTab === "2yr" && currentEngine.twoYearTopUpServicePlan &&
                    <p className="">
                      <span className="aui-headline-5">2-Year Top-up Service Plan <sup>***</sup>:</span>{` `}
                      <span className="aui-headline-4">{currentEngine.twoYearTopUpServicePlan}</span>
                    </p>
                  }
                  {isEtron && <p className="mt-2">
                    <a href="https://www.audi.com.au/au/web/en/owners/service/audi-genuine-care-service-plans.html#layer=/au/web/en/terms-and-conditions/e-tron-servicing.html" target="_top">Terms & Conditions</a>
                  </p>}
                </div>

                {currentEngine && <div>
                  {currentEngine.audiAdvantage && activeTab === "adv" && <div>
                    <p>2 Year unlimited km Warranty^^</p>
                    <p>2 Years of Scheduled Servicing†</p>
                    <p>2 Years of Roadside Assistance‡</p>
                  </div>}
                  {currentEngine.serviceDetails && currentEngine.serviceDetails.length > 0 && activeTab !== "adv" && <div className="aui-table">
                    <table style={{width: '100%'}}>
                      <thead>
                        {activeTab === "5yr" && <tr>
                          <th>Service Interval<sup>#1</sup></th>
                          <th>Service Plan Inclusions</th>
                        </tr>}
                        {activeTab === "2yr" && <tr>
                          <th>Service Interval<sup>#2</sup></th>
                          <th>Service Plan Inclusions</th>
                        </tr>}
                        {(activeTab === "3yr" || activeTab === "6yr") && <tr>
                          <th>Service Interval^</th>
                          <th>Service Plan Inclusions</th>
                        </tr>}
                      </thead>
                      <tbody>
                        {currentEngine.serviceDetails.map((row, i) => {
                          if (activeTab !== "2yr" || i + 1 > currentEngine.serviceDetails.length - 2) {
                            return (
                              <tr key={'sd' + i}>
                                <td>{row.servicePlan}</td>
                                <td dangerouslySetInnerHTML={{ __html: row.servicePlanInclusions }} />
                              </tr>
                            );
                          }
                          return null;
                        })}
                      </tbody>
                    </table>
                  </div>}
                </div>}
              </div>}
            </>}

          </div>
        </div>
      </main>
    </Layout>
  );
}

export default ServicePlanCalculator;
